/**
 * Follow a game code into the club that owns it.
 *
 * Every game API is scoped to the caller's CURRENT club, so a game code that
 * belongs to another club answers 404 — "Game not found: SKC@FCC (acting as
 * BU)". For staff, who reach every club, that 404 is not a permission
 * boundary, it is the wrong club being selected. This asks the server to
 * select the right one.
 *
 * The switch is a SERVER act: /auth/switch-to-game resolves the code against
 * the clubs the caller may enter and mints a new token cookie. It refuses to
 * guess when a code is held by more than one club, and a club the caller
 * cannot enter reads as absent rather than as "exists but not yours" — so
 * this cannot be used to discover another club's games.
 *
 * There is no loop risk: after a switch the caller reloads and the game
 * resolves, and a second attempt from the new club answers switched: false.
 */
import { api } from './api'

export type AlignResult = {
  /** A new club is in force; the caller should reload or refetch. */
  switched: boolean
  /** The code is held by several clubs the caller may enter; nothing changed. */
  choices?: { organization_id: number; organization_code: string; organization_name: string }[]
}

/** Ask the server to put us in the club that holds `gameCode`.
 *
 *  Never throws: this runs on a path that has ALREADY failed, and an error
 *  here must leave the caller's own message standing rather than replace it
 *  with a second, less useful one. */
export async function alignToGame(gameCode: string): Promise<AlignResult> {
  try {
    const r = await api<AlignResult>('/api/v1/auth/switch-to-game', {
      method: 'POST',
      body: JSON.stringify({ game_code: gameCode }),
    })
    return { switched: Boolean(r?.switched), choices: r?.choices }
  } catch {
    // 404 — no club we may enter holds this code — and every transport
    // failure land here alike: nothing changed, and the caller says so.
    return { switched: false }
  }
}

/** Ask the server to put a staff member in the club that owns report
 *  `runId` (a shared /insights/reports/<id> link). True when a new club is
 *  in force and the caller should reload. A club user, an unknown report, or
 *  the report already being this club's all answer false. Never throws, as
 *  alignToGame: the viewer's own "not found" stands when nothing changed. */
export async function alignToReport(runId: number): Promise<boolean> {
  try {
    const r = await api<{ switched?: boolean }>('/api/v1/auth/switch-to-report', {
      method: 'POST',
      body: JSON.stringify({ run_id: runId }),
    })
    return Boolean(r?.switched)
  } catch {
    return false
  }
}

/** The query parameter a link uses to name which club's copy of a game to
 *  open (Layers' Match Video): codes are not unique across clubs, so the
 *  server cannot always pick the owner alone. */
export const CLUB_PARAM = 'club'

/** Switch this session into club `clubId`. True when the switch landed.
 *  Never throws, for the same reason as alignToGame: the caller's own path
 *  carries on either way, and the match page's own 404 handling still runs
 *  when the switch could not be made. */
export async function switchToClub(clubId: number): Promise<boolean> {
  try {
    const r = await api<{ organization_id?: number }>(
      '/api/v1/auth/switch-organization', {
        method: 'POST',
        body: JSON.stringify({ organization_id: clubId }),
      })
    return r?.organization_id === clubId
  } catch {
    return false
  }
}
